// VexFlow staff rendering of a piano-drills `drill` (see src/lib/pianoDrills.js
// buildDrill). One measure per drill bar, 4 measures per row, wrapping onto
// additional rows for longer progressions. When `drill.bassId` is "off" this
// is a single auto-clef stave; otherwise it's a grand staff — a treble-ish
// stave (clef chosen from the right-hand voicings, in case they sit low) on
// top of a bass-clef stave carrying the left hand, per measure. Purely
// presentational: the parent owns `drill`, `currentEventIndices` (guide-mode
// playback cursor, one index per hand) and `statuses` (score-mode hit/miss
// per event index).
//
// Redraw-from-scratch happens only when `drill` changes (a new key,
// progression, rhythm, bass pattern, or range). Highlighting the
// currently-sounding / scored event is done as a separate, cheap pass: after
// drawing we keep a parallel array of the VexFlow StaveNote objects (one per
// drill event, regardless of which stave/hand it belongs to) and, on every
// currentEventIndices/statuses change, mutate the fill/stroke of the
// already-drawn SVG nodes directly (via StaveNote#getSVGElement) instead of
// re-running VexFlow's layout/draw — safe to do many times a second.

import React, { useEffect, useRef, useState } from "react";
import { midiToVexKey, DUR_TO_VEX, clefForMidis } from "../../lib/pianoDrills";
import { cn } from "../../lib/utils";

// VexFlow (metrics.ts) calls the global `structuredClone` once, at import
// time, to clone its default engraving-metrics config. Every real browser
// target for this app has it, but CRA's Jest/jsdom test environment does
// not expose it on the jsdom global scope — only on Node's. A static
// `import "vexflow"` would run before we get a chance to polyfill it (ES
// import declarations are hoisted above ordinary statements), so we load
// VexFlow via `require` after installing the polyfill, keeping both on the
// same synchronous, unhoisted execution path.
/* global globalThis -- CRA's eslint env predates the globalThis global */
if (typeof globalThis.structuredClone !== "function") {
  globalThis.structuredClone = (value) =>
    value === undefined ? value : JSON.parse(JSON.stringify(value));
}
// eslint-disable-next-line global-require, import/no-commonjs
const {
  Renderer,
  Stave,
  StaveConnector,
  StaveNote,
  Voice,
  Formatter,
  Beam,
  Accidental,
  Annotation,
  Dot,
} = require("vexflow");

// StaveNote for a drill event. VexFlow parses dotted duration codes ("qd")
// for timing but only draws the dot glyph when it's attached explicitly.
const eventStaveNote = ({ clef, event, useFlats, midiToVexKey, durToVex }) => {
  const duration = durToVex[event.durSteps] || "q";
  const note = new StaveNote({
    clef,
    keys: event.midis.map((midi) => midiToVexKey(midi, useFlats)),
    duration,
    autoStem: true,
  });
  if (duration.endsWith("d")) Dot.buildAndAttach([note], { all: true });
  return note;
};

const MEASURE_WIDTH = 230;
const FIRST_MEASURE_EXTRA = 100; // room for clef + time sig + key sig
const MEASURES_PER_ROW = 4;
const ROW_HEIGHT = 150; // single-stave row (bass off)
const GRAND_STAVE_GAP = 110; // treble-stave-y to bass-stave-y within a row
const GRAND_ROW_HEIGHT = ROW_HEIGHT + GRAND_STAVE_GAP; // grand-staff row
const TOP_PADDING = 40; // room for the chord-symbol annotation above the staff
const SIDE_PADDING = 12;
const BOTTOM_PADDING = 30;

const HIGHLIGHT_COLORS = {
  current: "#4f46e5", // indigo-600 — guide-mode "sounding now"
  hit: "#059669", // emerald-600
  miss: "#dc2626", // red-600
};

// Paint (or reset) every fill/stroke-bearing descendant of a drawn StaveNote's
// SVG group. VexFlow's SVG backend sets `fill`/`stroke` as SVG presentation
// attributes on individual path/text nodes when it draws them; setting the
// same-named CSS property on those nodes overrides the attribute without
// touching VexFlow's own state, so nothing here forces a re-layout.
const paintNote = (svgEl, color) => {
  const nodes = svgEl.querySelectorAll("path, rect, ellipse, circle, text");
  nodes.forEach((node) => {
    node.style.fill = color || "";
    node.style.stroke = color || "";
  });
};

export default function SheetView({ drill, currentEventIndices, statuses }) {
  const containerRef = useRef(null);
  const notesRef = useRef([]); // StaveNote[] parallel to drill.events
  const [renderTick, setRenderTick] = useState(0);
  const [broken, setBroken] = useState(false);

  const hasBars = Boolean(drill && !drill.error && drill.bars && drill.bars.length > 0);
  const showPlaceholder = !hasBars || broken;

  // Redraw from scratch whenever the drill itself changes.
  useEffect(() => {
    if (!hasBars || !containerRef.current) return undefined;
    const container = containerRef.current;
    container.innerHTML = "";
    notesRef.current = [];

    try {
      const { bars, events, keyName, useFlats, bassId } = drill;
      const bassOn = Boolean(bassId) && bassId !== "off";
      // Right-hand clef, chosen from the bars' right-hand voicings so a
      // low-sitting right hand still gets a bass clef on the top stave.
      const clef = clefForMidis(bars.flatMap((bar) => bar.midis));
      const rowHeight = bassOn ? GRAND_ROW_HEIGHT : ROW_HEIGHT;

      const rows = [];
      for (let i = 0; i < bars.length; i += MEASURES_PER_ROW) {
        rows.push(bars.slice(i, i + MEASURES_PER_ROW));
      }
      const rowWidth = (row) => FIRST_MEASURE_EXTRA + MEASURE_WIDTH * row.length;
      const totalWidth = SIDE_PADDING * 2 + Math.max(...rows.map(rowWidth));
      const totalHeight = TOP_PADDING + rows.length * rowHeight + BOTTOM_PADDING;

      const renderer = new Renderer(container, Renderer.Backends.SVG);
      renderer.resize(totalWidth, totalHeight);
      const context = renderer.getContext();

      const notesByEventIndex = new Array(events.length);
      let eventCursor = 0;

      rows.forEach((row, rowIdx) => {
        row.forEach((bar, colIdx) => {
          const barIdx = rowIdx * MEASURES_PER_ROW + colIdx;
          const isRowStart = colIdx === 0;
          const width = isRowStart ? FIRST_MEASURE_EXTRA + MEASURE_WIDTH : MEASURE_WIDTH;
          const x = isRowStart
            ? SIDE_PADDING
            : SIDE_PADDING + FIRST_MEASURE_EXTRA + MEASURE_WIDTH + (colIdx - 1) * MEASURE_WIDTH;
          const y = TOP_PADDING + rowIdx * rowHeight;

          const trebleStave = new Stave(x, y, width);
          if (isRowStart) {
            trebleStave.addClef(clef);
            trebleStave.addTimeSignature("4/4");
            trebleStave.addKeySignature(keyName);
          }
          trebleStave.setContext(context).draw();

          let bassStave = null;
          if (bassOn) {
            bassStave = new Stave(x, y + GRAND_STAVE_GAP, width);
            if (isRowStart) {
              bassStave.addClef("bass");
              bassStave.addTimeSignature("4/4");
              bassStave.addKeySignature(keyName);
            }
            bassStave.setContext(context).draw();

            if (isRowStart) {
              // Nice-to-have: brace + single left line joining the two
              // staves. Purely cosmetic — skip quietly if it ever throws.
              try {
                new StaveConnector(trebleStave, bassStave)
                  .setType("brace")
                  .setContext(context)
                  .draw();
                new StaveConnector(trebleStave, bassStave)
                  .setType("singleLeft")
                  .setContext(context)
                  .draw();
              } catch (connectorErr) {
                console.warn("SheetView: StaveConnector failed, skipping.", connectorErr);
              }
            }
          }

          const barEvents = [];
          while (eventCursor < events.length && events[eventCursor].bar === barIdx) {
            barEvents.push({ event: events[eventCursor], index: eventCursor });
            eventCursor += 1;
          }
          const rhBarEvents = barEvents.filter(({ event }) => event.hand !== "lh");
          const lhBarEvents = bassOn ? barEvents.filter(({ event }) => event.hand === "lh") : [];

          const rhNotes = rhBarEvents.map(({ event }) =>
            eventStaveNote({ clef, event, useFlats, midiToVexKey, durToVex: DUR_TO_VEX })
          );
          rhNotes.forEach((note, i) => {
            notesByEventIndex[rhBarEvents[i].index] = note;
          });

          if (rhNotes.length) {
            rhNotes[0].addModifier(
              new Annotation(bar.label).setVerticalJustification(Annotation.VerticalJustify.TOP)
            );
          }

          const lhNotes = lhBarEvents.map(({ event }) =>
            eventStaveNote({ clef: "bass", event, useFlats, midiToVexKey, durToVex: DUR_TO_VEX })
          );
          lhNotes.forEach((note, i) => {
            notesByEventIndex[lhBarEvents[i].index] = note;
          });

          const trebleVoice = new Voice({ numBeats: 4, beatValue: 4 }).setStrict(false);
          trebleVoice.addTickables(rhNotes);
          const voices = [trebleVoice];

          let bassVoice = null;
          if (bassOn) {
            bassVoice = new Voice({ numBeats: 4, beatValue: 4 }).setStrict(false);
            bassVoice.addTickables(lhNotes);
            voices.push(bassVoice);
          }

          voices.forEach((voice) => Accidental.applyAccidentals([voice], keyName));

          const formatWidth = Math.max(
            20,
            trebleStave.getNoteEndX() - trebleStave.getNoteStartX() - 10
          );
          const formatter = new Formatter();
          voices.forEach((voice) => formatter.joinVoices([voice]));
          formatter.format(voices, formatWidth);

          trebleVoice.draw(context, trebleStave);
          if (bassVoice) bassVoice.draw(context, bassStave);

          // LH whole/half bass notes never beam; only the RH rhythm does.
          Beam.generateBeams(rhNotes).forEach((beam) => {
            beam.setContext(context).draw();
          });
        });
      });

      notesRef.current = notesByEventIndex;
      setBroken(false);
    } catch (err) {
      console.warn("SheetView: VexFlow rendering failed, showing placeholder instead.", err);
      container.innerHTML = "";
      notesRef.current = [];
      setBroken(true);
    }
    setRenderTick((t) => t + 1);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill, hasBars]);

  // Cheap post-draw highlight pass — no VexFlow re-layout involved.
  useEffect(() => {
    const notes = notesRef.current;
    if (!notes || !notes.length) return;
    notes.forEach((note, index) => {
      if (!note) return;
      const svgEl = note.getSVGElement && note.getSVGElement();
      if (!svgEl) return;
      const status = statuses ? statuses[index] : undefined;
      let color = null;
      if (status === "hit") color = HIGHLIGHT_COLORS.hit;
      else if (status === "miss") color = HIGHLIGHT_COLORS.miss;
      else if (currentEventIndices && currentEventIndices.includes(index)) color = HIGHLIGHT_COLORS.current;
      paintNote(svgEl, color);
    });
    // renderTick makes this rerun right after a fresh draw (new note objects).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEventIndices, statuses, renderTick]);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 shadow-sm">
      {showPlaceholder && (
        <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-amber-300 text-amber-700/70">
          Pick some chords to see the sheet
        </div>
      )}
      {/* Keep the canvas mounted even while the placeholder shows, so a
          failed draw can recover when the next drill renders fine. */}
      <div className={cn("overflow-x-auto", showPlaceholder && "hidden")}>
        <div ref={containerRef} className={cn("min-w-max")} />
      </div>
    </div>
  );
}
